import {
  refFarmViewFunction,
  refFarmBoostViewFunction,
  REF_FI_CONTRACT_ID,
  near,
  refFiViewFunction,
} from './near';
import { getCurrentWallet } from '../utils/wallets-integration';
import db, { TokenPrice, BoostSeeds } from '../store/RefDatabase';
import { getPoolsByIds, getTokenPriceList } from './indexer';
import { Seed, FarmBoost } from './farmBoostTypes';
import { PoolRPCView } from './api';
import { listPools, PoolInfo } from './swapV3';
import getConfig from './config';

const config = getConfig();
const { REF_UNI_V3_SWAP_CONTRACT_ID } = getConfig();

// boost farm related function start
export const list_seeds_info = async () => {
  return await refFarmBoostViewFunction({
    methodName: 'list_seeds_info',
  });
};

export const get_seed = async (seed_id: string) => {
  return await refFarmBoostViewFunction({
    methodName: 'get_seed',
    args: { seed_id },
  });
};

export const list_seed_farms = async (seed_id: string) => {
  try {
    return await refFarmBoostViewFunction({
      methodName: 'list_seed_farms',
      args: { seed_id },
    });
  } catch {
    return null;
  }
};

export const list_farmer_seeds = async () => {
  const accountId = getCurrentWallet().wallet.getAccountId();
  // Signed out there are no seeds, and the contract rejects an empty farmer_id.
  if (!accountId) return {};
  return await refFarmBoostViewFunction({
    methodName: 'list_farmer_seeds',
    args: { farmer_id: accountId },
  });
};

export const get_unclaimed_rewards = async (seed_id: string) => {
  const accountId = getCurrentWallet().wallet.getAccountId();
  return await refFarmBoostViewFunction({
    methodName: 'get_unclaimed_rewards',
    args: { farmer_id: accountId, seed_id },
  });
};

export const get_config = async () => {
  return await refFarmBoostViewFunction({
    methodName: 'get_config',
  });
};

export const get_unWithDraw_rewards = async () => {
  const accountId = getCurrentWallet().wallet.getAccountId();
  return await refFarmBoostViewFunction({
    methodName: 'list_farmer_rewards',
    args: { farmer_id: accountId },
  });
};

export const getServerTime = async () => {
  const result = await near.connection.provider
    .block({
      finality: 'final',
    })
    .catch(() => {
      return {
        header: {
          timestamp: new Date().getTime() * 100000,
        },
      };
    });
  const timestamp = result?.header?.timestamp;
  return timestamp;
};

export const getBoostTokenPrices = async (): Promise<
  Record<string, TokenPrice>
> => {
  try {
    let tokenPrices: Record<string, TokenPrice> = {};
    const cacheData = await db.checkTokenPrices();
    if (cacheData) {
      const cachedPrices: TokenPrice[] = await db.queryTokenPrices();
      cachedPrices.forEach((price: TokenPrice) => {
        const { id, update_time, ...priceInfo } = price;
        tokenPrices[id] = priceInfo;
      });
      getBoostTokenPricesFromServer();
    } else {
      tokenPrices = await getBoostTokenPricesFromServer();
    }
    return tokenPrices;
  } catch (error) {
    return {};
  }
};

const getBoostTokenPricesFromServer = async (): Promise<
  Record<string, TokenPrice>
> => {
  try {
    const tokenPrices: Record<string, TokenPrice> = await getTokenPriceList();
    await db.cacheTokenPrices(tokenPrices);
    return tokenPrices;
  } catch (error) {
    return {};
  }
};

interface BoostSeedsFarmsAndPools {
  seeds: Seed[];
  farms: FarmBoost[][];
  pools: PoolRPCView[] & PoolInfo[];
}

// Callers destructure the result, so a failed load yields empty lists rather than null.
const getEmptyBoostSeedsFarmsAndPools = (): BoostSeedsFarmsAndPools => ({
  seeds: [],
  farms: [],
  pools: [],
});

export const getBoostSeeds = async (): Promise<BoostSeedsFarmsAndPools> => {
  try {
    const seeds: Seed[] = [];
    const farms: FarmBoost[][] = [];
    const pools: PoolRPCView[] & PoolInfo[] = [];
    const cacheData = await db.checkBoostSeeds();
    if (cacheData) {
      const cachedBoostSeeds: BoostSeeds[] = await db.queryBoostSeeds();
      cachedBoostSeeds.forEach((cachedBoostSeed: BoostSeeds) => {
        const { id, update_time, ...seedDetails } = cachedBoostSeed;
        const { seed, farmList, pool } = seedDetails;
        seeds.push(seed);
        farms.push(farmList);
        if (pool) {
          pools.push(pool);
        }
      });
      getBoostSeedsFromServer();
      return { seeds, farms, pools };
    } else {
      const result = await getBoostSeedsFromServer();
      return result;
    }
  } catch (error) {
    console.error('Failed to load boost farm seeds', error);
    return getEmptyBoostSeedsFarmsAndPools();
  }
};

const getBoostSeedsFromServer = async (): Promise<BoostSeedsFarmsAndPools> => {
  try {
    // get all seeds
    let list_seeds = await list_seeds_info();
    // not the classic and dcl seeds would be filtered
    list_seeds = list_seeds.filter((seed: Seed) => {
      const contract_id = seed.seed_id.split('@')?.[0];
      return (
        contract_id == REF_UNI_V3_SWAP_CONTRACT_ID ||
        contract_id == REF_FI_CONTRACT_ID
      );
    });
    // get all farms
    const farmsPromiseList: Promise<any>[] = [];
    const poolIds = new Set<string>();
    const dcl_poolIds = new Set<string>();
    // get all dcl pools
    const dcl_all_pools: PoolInfo[] = await listPools();
    let pools: any[] = [];
    const both_normalPools_dclPools: any[] = [];
    list_seeds.forEach((seed: Seed) => {
      const { seed_id } = seed;
      // seed type: [commonSeed, loveSeed, dclSeed]
      const [contractId, tempPoolId] = seed_id.split('@');
      if (tempPoolId && contractId !== REF_UNI_V3_SWAP_CONTRACT_ID) {
        poolIds.add(tempPoolId);
      } else if (tempPoolId && contractId == REF_UNI_V3_SWAP_CONTRACT_ID) {
        const [fixRange, dcl_pool_id, left_point, right_point] =
          tempPoolId.split('&');
        dcl_poolIds.add(dcl_pool_id);
      }
      farmsPromiseList.push(list_seed_farms(seed_id));
    });
    const list_farms: FarmBoost[][] = await Promise.all(farmsPromiseList);
    let cacheFarms: FarmBoost[] = [];
    list_farms.forEach((farmsOfSeed: FarmBoost[]) => {
      cacheFarms = cacheFarms.concat(farmsOfSeed);
    });
    pools = await getPoolsByIds({ pool_ids: Array.from(poolIds) });
    // cache seeds farms pools
    const cacheSeedsFarmsPools: any[] = [];
    list_seeds.forEach((seed: Seed, index: number) => {
      let pool: any = null;
      const [contractId, tempPoolId] = seed.seed_id.split('@');
      if (tempPoolId) {
        if (contractId == REF_UNI_V3_SWAP_CONTRACT_ID) {
          const [fixRange, dcl_pool_id, left_point, right_point] =
            tempPoolId.split('&');
          pool = dcl_all_pools.find((dclPool: PoolInfo) => {
            if (dclPool.pool_id == dcl_pool_id) return true;
          });
        } else {
          const id = tempPoolId;
          pool = pools.find((classicPool: any) => {
            if (+classicPool.id == +id) return true;
          });
        }
      }
      cacheSeedsFarmsPools.push({
        id: seed.seed_id,
        seed,
        farmList: list_farms[index],
        pool,
      });
      if (pool) {
        both_normalPools_dclPools.push(pool);
      }
    });
    db.cacheBoostSeeds(cacheSeedsFarmsPools);
    return {
      seeds: list_seeds,
      farms: list_farms,
      pools: both_normalPools_dclPools,
    };
  } catch (error) {
    console.error('Failed to load boost farm seeds', error);
    return getEmptyBoostSeedsFarmsAndPools();
  }
};

export const getVeSeedShare = async (): Promise<any> => {
  // REF_VE_CONTRACT_ID
  return await fetch(
    config.sodakiApiUrl + `/seedv2/v2.ref-finance.near@79/accounts`,
    {
      method: 'GET',
    }
  )
    .then((response) => response.json())
    .then((responseBody) => {
      return responseBody;
    })
    .catch(() => {
      return {};
    });
};

// migrate related methods end
export const get_seed_info = async (seed_id: string): Promise<any> => {
  return refFarmViewFunction({
    methodName: 'get_seed_info',
    args: { seed_id },
  });
};

export const get_shadow_records = () => {
  const account_id = getCurrentWallet()?.wallet?.getAccountId();
  // Signed out: nothing to look up (the contract rejects an empty account id).
  if (!account_id) return Promise.resolve({});
  return refFiViewFunction({
    methodName: 'get_shadow_records',
    args: { account_id },
  });
};
